import {
  fetchTypeStatusForCommunications,
  getAllLayoutRecordsBasedOnService,
  getWorkflowByEntityNameAndRecordId,
  hideLoader,
  procedureCallCorrespondingToTenant,
  showLoader,
  updateWorkflowDefinition,
  updateWorkflowInstance,
  WorkflowInstanceTaskType,
  WorkflowInstanceType,
  WorkflowProcessType,
} from '@app-store'
import {
  ENTITY_NAME,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
  RECORD_STATE,
} from '../../../utils/constants'
import { AppDispatch } from '../../../utils/agent.store'
import { editEntityDataEmbeddedAPI } from '../../../apis/entityData.api'

export const loadCaseWorkflow = async (
  dispatch: AppDispatch,
  caseId: string,
  options?: {
    layoutIdOverride?: string
    useIntakeLayout?: boolean
    /** Skip global loader overlay (e.g. modal tab switches). */
    silent?: boolean
  }
): Promise<{
  process?: WorkflowProcessType
  workflow_instance?: WorkflowInstanceType
} | null> => {
  if (!options?.silent) {
    dispatch(showLoader())
  }
  try {
    const response = await getWorkflowByEntityNameAndRecordId({
      recordId: caseId,
      entityName: ENTITY_NAME.CASE?.toLowerCase(),
    })
    const data = response?.data
    if (!data) return null

    const { workflow_instance, process } = data
    let updatedInstance = workflow_instance
    const serviceId = process?.service_id

    if (serviceId && options?.useIntakeLayout) {
      const layoutRecords = await getAllLayoutRecordsBasedOnService(serviceId)
      const layoutData = layoutRecords?.data
      const intakeLayoutId =
        layoutData?.caseTypeLayoutsPage?.intake_layout_id?.id
      const defaultEditLayoutId = layoutData?.defaultLayouts?.find(
        (layout: { type: string; id: number }) =>
          layout.type === LAYOUT_TYPE_NAMESPACE.VIEW_EDIT
      )?.id
      const layoutIdToUse =
        options.layoutIdOverride ?? intakeLayoutId ?? defaultEditLayoutId

      if (layoutIdToUse && updatedInstance?.tasks) {
        updatedInstance = {
          ...updatedInstance,
          tasks: updatedInstance.tasks.map(
            (task: WorkflowInstanceTaskType, index: number) =>
              index === 0
                ? {
                    ...task,
                    identifier: {
                      ...task.identifier,
                      layout_info: {
                        ...task.identifier?.layout_info,
                        layout_id: layoutIdToUse,
                        layout_type: LAYOUT_TYPE_NAMESPACE.VIEW_EDIT_TYPE,
                      },
                    },
                  }
                : task
          ),
        }
      }
    }

    dispatch(updateWorkflowDefinition(process))
    dispatch(updateWorkflowInstance(updatedInstance))

    return { process, workflow_instance: updatedInstance }
  } catch (err) {
    console.error(err)
    return null
  } finally {
    if (!options?.silent) {
      dispatch(hideLoader())
    }
  }
}

export const updateHasDedupeResolved = async (communicationId: string) => {
  try {
    const data = {
      has_dedup_resolved: true,
      _state: RECORD_STATE.CHANGED,
    }
    await editEntityDataEmbeddedAPI(
      ENTITY_NAME.HCP_PORTAL_ENTITY,
      communicationId ?? '',
      data,
      ['']
    )
  } catch (err) {
    console.error(err)
  }
}

export const updateEnrollmentStatus = async (
  communicationId: string,
  status: string,
  changeHasDedupeResolved?: boolean
) => {
  try {
    const response = await fetchTypeStatusForCommunications(
      NAMESPACES.WEB_ENROLLMENT_STATUS
    )
    const webEnrollmentStatusId = response?.data?.find(
      (item: { value: string }) => item?.value === status
    )?.id
    if (webEnrollmentStatusId) {
      if (!communicationId) return
      // or validate before parseInt:
      const id = parseInt(communicationId, 10)
      if (isNaN(id)) return
      await editEntityDataEmbeddedAPI(
        ENTITY_NAME.HCP_PORTAL_ENTITY,
        parseInt(communicationId ?? ''),
        {
          enrollment_status: {
            id: webEnrollmentStatusId,
            _state: RECORD_STATE.CHANGED,
          },
          reason: '',
          ...(changeHasDedupeResolved && {
            has_dedupe_resolved: true,
          }),
        },
        ['']
      )
    }
  } catch (err) {
    console.error(err)
  }
}

export const procedureCallForCaseAttachment = async (
  newCaseId: number | null,
  attachedCaseId: number,
  selectedTenantId: string | number,
  outcomeId?: number | null,
  outcomeReasonId?: number | null,
  intakeChannelId?: number | null,
  communicationId?: number | null,
  requestorId?: number | null,
  documentTypeId?: number | null,
  isInteraction?: boolean,
  enrollmentId?: string | null
) => {
  const payload = {
    params: {
      p_existing_case_id: {
        type: 'LONGDATATYPE',
        order: 1,
        value: attachedCaseId,
        is_input_param: true,
      },
      p_new_case_id: {
        type: 'LONGDATATYPE',
        order: 2,
        value: newCaseId,
        is_input_param: true,
      },
      p_outcome_id: {
        type: 'LONGDATATYPE',
        order: 3,
        value: outcomeId ?? null,
        is_input_param: true,
      },
      p_outcome_reason_id: {
        type: 'LONGDATATYPE',
        order: 4,
        value: outcomeReasonId ?? null,
        is_input_param: true,
      },
      p_intake_channel_id: {
        type: 'LONGDATATYPE',
        order: 5,
        value: intakeChannelId ?? null,
        is_input_param: true,
      },
      p_communication_id: {
        type: 'LONGDATATYPE',
        order: 6,
        value: communicationId ?? null,
        is_input_param: true,
      },
      p_requestor_id: {
        type: 'LONGDATATYPE',
        order: 7,
        value: requestorId ?? null,
        is_input_param: true,
      },
      p_document_type_id: {
        type: 'LONGDATATYPE',
        order: 8,
        value: documentTypeId ?? null,
        is_input_param: true,
      },
      p_is_interaction: {
        type: 'BOOLEANDATATYPE',
        order: 9,
        value: isInteraction ?? false,
        is_input_param: true,
      },
      p_enrollment_id: {
        type: 'STRINGDATATYPE',
        order: 10,
        value: enrollmentId ?? null,
        is_input_param: true,
      },
      p_result_case_id: {
        type: 'LONGDATATYPE',
        order: 11,
        value: null,
        is_input_param: false,
      },
      p_result_task_id: {
        type: 'LONGDATATYPE',
        order: 12,
        value: null,
        is_input_param: false,
      },
      p_result_audit_id: {
        type: 'LONGDATATYPE',
        order: 13,
        value: null,
        is_input_param: false,
      },
      p_result_message: {
        type: 'TEXTDATATYPE',
        order: 14,
        value: null,
        is_input_param: false,
      },
    },
    procedureName: 'process_multichannel_attach_case_latest',
    primary_key_name: 'p_result_case_id',
  }
  await procedureCallCorrespondingToTenant(
    selectedTenantId?.toString() ?? '',
    payload
  )
}
